import { diffRewards, FANWAVE_MODE, WAVE_TIMING } from './constants';

/**
 * The shapes whose CLOSE is a wall-clock time, so an edit to it travels on `endsAt`.
 *
 * Includes option B (`second_half_until`): its open is an anchor, but its close is an absolute
 * time like the other two — which is the only edge a LIVE wave may move, and it has to move on the
 * legacy field because PUT /update refuses an anchor pair on a running wave outright.
 */
const CLOCK_ENDED_SHAPES = [
  WAVE_TIMING.CLOCK_WINDOW,
  WAVE_TIMING.HALF_TIME_UNTIL,
  WAVE_TIMING.SECOND_HALF_UNTIL
];

/** Of those, the ones whose OPEN comes off the match clock — so no `startsAt` of their own. */
const MATCH_OPEN_CLOCK_END_SHAPES = [WAVE_TIMING.HALF_TIME_UNTIL, WAVE_TIMING.SECOND_HALF_UNTIL];

/**
 * The PUT /admin/fanwave/update body for an edited wave — pure, so the diffing rules can be
 * reasoned about (and tested) away from the form.
 *
 * WHICH wave: `waveId`, in both modes. A match can hold several waves now, so `gameId` no
 * longer identifies one — the API accepts it only while a match has exactly one wave and
 * answers 409 otherwise. It is sent only for a legacy row that carries no waveId of its own.
 *
 * The endpoint patches only the keys it receives, which makes three rules load-bearing:
 *   - Game Day schedules are alternatives: sending one pair clears the other server-side, so a
 *     shape switch is just the new pair. Returning to the default clears the pair the wave uses
 *     TODAY — nulling both pairs is refused ("send one, not both").
 *   - A blank side of a chosen pair is an explicit null, not "leave it alone": the API merges an
 *     omitted field against the wave's current value, which would keep a minute the admin cleared.
 *   - A live wave's start is immutable and the API rejects the field even when unchanged, so it
 *     is never sent; only the end (in the terms the wave already runs on) is.
 *
 * Rewards differ by mode: a Game Day patch is merged over the wave's snapshot, so only changed
 * keys go; a Non-Game Day patch REPLACES the stored override, so it carries the wave's whole set
 * of deviations from the channel defaults ({} clears them).
 */
export const buildUpdatePayload = ({ mode, isLive, wave, original, current, configRewards }) => {
  const isGameDay = mode === FANWAVE_MODE.GAME_DAY;
  const o = original || {};
  const payload = { teamId: wave.teamId };
  if (wave.waveId) payload.waveId = wave.waveId;
  else payload.gameId = wave.gameId;

  // A live wave's timing shape is read off `current.shape` (the form seeds it from the list row
  // synchronously) rather than `o.shape`, which is only there once the channel config has
  // loaded — a clock-ended wave would otherwise be saved with an `endMinute`.
  const endsOnClock = CLOCK_ENDED_SHAPES.includes(current.shape);

  if (isLive) {
    if (!isGameDay || endsOnClock) {
      if (current.endsAt !== o.endsAt) payload.endsAt = fromDatetimeLocal(current.endsAt);
    } else if (current.shape === WAVE_TIMING.MINUTES && current.endMinute !== o.endMinute) {
      payload.endMinute = current.endMinute === '' ? null : Number(current.endMinute);
    }
    // A live wave on the default schedule sends no schedule field at all: an end minute would
    // turn it into a match-minute wave, and its close belongs to the channel's window.
  } else if (isGameDay) {
    if (current.shape === WAVE_TIMING.MINUTES) {
      if (
        o.shape !== WAVE_TIMING.MINUTES ||
        current.startMinute !== o.startMinute ||
        current.endMinute !== o.endMinute
      ) {
        payload.startMinute = current.startMinute === '' ? null : Number(current.startMinute);
        payload.endMinute = current.endMinute === '' ? null : Number(current.endMinute);
      }
    } else if (current.shape === WAVE_TIMING.CLOCK_WINDOW) {
      if (
        o.shape !== WAVE_TIMING.CLOCK_WINDOW ||
        current.startsAt !== o.startsAt ||
        current.endsAt !== o.endsAt
      ) {
        payload.startsAt = fromDatetimeLocal(current.startsAt);
        payload.endsAt = fromDatetimeLocal(current.endsAt);
      }
    } else if (MATCH_OPEN_CLOCK_END_SHAPES.includes(current.shape)) {
      // A match-clock open with a wall-clock end: `endsAt` alone IS the schedule. The start is
      // sent as an explicit null — an omitted one is inherited, which would keep a clock start the
      // wave is no longer meant to have.
      //
      // Option B (the second-half open) goes through here too, because the two shapes differ only
      // in WHICH whistle opens the wave — a distinction the legacy fields cannot carry at all. The
      // caller upgrades this pair to the anchor pair that states the open, which is why the wave's
      // own shape must reach the payload as a real change (`endsAt`) rather than as nothing.
      if (o.shape !== current.shape || current.endsAt !== o.endsAt) {
        payload.startsAt = null;
        payload.endsAt = fromDatetimeLocal(current.endsAt);
      }
    } else if (o.shape === WAVE_TIMING.MINUTES) {
      payload.startMinute = null;
      payload.endMinute = null;
    } else if (CLOCK_ENDED_SHAPES.includes(o.shape)) {
      payload.startsAt = null;
      payload.endsAt = null;
    }
  } else {
    if (current.startsAt !== o.startsAt) payload.startsAt = fromDatetimeLocal(current.startsAt);
    if (current.endsAt !== o.endsAt) payload.endsAt = fromDatetimeLocal(current.endsAt);
  }

  const targetChanged = Number(current.target) !== Number(o.target);
  if (targetChanged) payload.target = Number(current.target);

  if (diffRewards(o.rewards, current.rewards)) {
    payload.rewards = isGameDay
      ? diffRewards(o.rewards, current.rewards)
      : diffRewards(configRewards, current.rewards) || {};
  }

  // Auto restart is editable only while the wave is scheduled: once it has started its chain
  // rules are fixed and the API refuses the fields. A blank limit is an explicit null ("no
  // limit"), because an omitted field would keep the old one.
  if (!isLive) {
    if (!!current.autoRestart !== !!o.autoRestart) payload.autoRestart = !!current.autoRestart;
    if (current.autoRestart) {
      if (current.maxRestarts !== (o.maxRestarts ?? '')) {
        payload.maxRestarts = current.maxRestarts === '' ? null : Number(current.maxRestarts);
      }
      if (current.minRemaining !== (o.minRemaining ?? '')) {
        payload.minRemainingMinutes =
          current.minRemaining === '' ? null : Number(current.minRemaining);
      }
    }
  }

  // teamId and the wave key are identity, not changes — the API needs at least one real change.
  const changed = Object.keys(payload).filter((k) => !['teamId', 'waveId', 'gameId'].includes(k));
  return { payload, changed, targetChanged };
};

/** `datetime-local` (the admin's timezone) → the ISO instant the API stores. */
export function fromDatetimeLocal(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** ISO (UTC) → the `datetime-local` value in the admin's own timezone, for edit prefills. */
export const toDatetimeLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};
